import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { MenuIcon, PhoneIcon, XIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { BUSINESS } from '../data/i18n';
import { Logo } from './Logo';
const links = [{
  href: '#services',
  labelKey: 'nav.services'
}, {
  href: '#gallery',
  labelKey: 'nav.gallery'
}, {
  href: '#about',
  labelKey: 'nav.about'
}, {
  href: '#contact',
  labelKey: 'nav.contact'
}];
export function Navbar() {
  const {
    t,
    lang,
    setLang
  } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);
  return <header className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,border-color] duration-200 ease-smooth ${scrolled || open ? 'border-b border-navy-900/10 bg-white/95 shadow-[0_2px_16px_-8px_rgba(0,31,63,0.25)] backdrop-blur' : 'border-b border-transparent bg-white'}`} data-id="element-220" data-mp="9o7qdn844sgj">
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between gap-4 px-4 sm:px-6 lg:px-8" data-id="element-221" data-mp="3n3w0wehin9mv">
        <Logo variant="dark" data-id="element-222" data-mp="3s1nure1q15tp" />

        <nav aria-label="Main" className="hidden items-center gap-1 lg:flex" data-id="element-223" data-mp="3s2qhravxao4c">
          {links.map(link => <a key={link.href} href={link.href} className="rounded-md px-3 py-2 text-sm font-semibold text-navy-900/75 transition-colors duration-150 ease-smooth hover:bg-navy-50 hover:text-navy-900" data-id="element-224" data-mp="u2xky9f56yd">
              {t(link.labelKey)}
            </a>)}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3" data-id="element-225" data-mp="31b03orpccqvr">
          <div className="hidden items-center rounded-md border border-navy-900/15 p-0.5 sm:flex" role="group" aria-label={t('nav.language')} data-id="element-226" data-mp="319gicmotvpn4">
            {(['en', 'es'] as const).map(code => <button key={code} type="button" onClick={() => setLang(code)} aria-pressed={lang === code} className={`rounded px-2 py-1 text-xs font-bold uppercase tracking-wide transition-colors duration-150 ease-smooth ${lang === code ? 'bg-navy-900 text-white' : 'text-navy-900/60 hover:text-navy-900'}`} data-id="element-227" data-mp="361ghicx2qein">
                {code}
              </button>)}
          </div>

          <a href={BUSINESS.phoneHref} className="hidden items-center gap-2 text-sm font-bold text-navy-900 transition-colors duration-150 ease-smooth hover:text-brand-600 xl:flex" data-id="element-228" data-mp="7mgxb2q5z4wd">
            <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-229" data-mp="f3vjfazxeze6" />
            {BUSINESS.phoneDisplay}
          </a>

          <a href="#contact" className="hidden rounded-md bg-amber-600 px-4 py-2.5 text-sm font-bold text-white shadow-sm transition-[background-color,transform] duration-150 ease-smooth hover:bg-amber-700 active:scale-[0.98] sm:block" data-id="element-230" data-mp="14dqqkjif1zj">
            {t('nav.cta')}
          </a>

          <button type="button" onClick={() => setOpen(v => !v)} aria-expanded={open} aria-label={open ? t('nav.close') : t('nav.menu')} className="flex h-10 w-10 items-center justify-center rounded-md border border-navy-900/15 text-navy-900 lg:hidden" data-id="element-231" data-mp="xumpa2kpg7nt">
            {open ? <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-232" data-mp="1bs1k5mtq97n6" /> : <MenuIcon className="h-5 w-5" aria-hidden="true" data-id="element-233" data-mp="1gmfgbrg7risw" />}
          </button>
        </div>
      </div>

      <AnimatePresence data-id="element-234" data-mp="3ozudtv3c5mss">
        {open ? <motion.div key="mobile-menu" initial={{
        opacity: 0,
        y: -8
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -8
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} className="border-t border-navy-900/10 bg-white lg:hidden" data-id="element-235" data-mp="3k2kqqm3f5n84">
            <nav aria-label="Mobile" className="mx-auto max-w-7xl px-4 py-4 sm:px-6" data-id="element-236" data-mp="bmizakwizgii">
              <ul className="flex flex-col" data-id="element-237" data-mp="6p8xe5ry9cds">
                {links.map(link => <li key={link.href} data-id="element-238" data-mp="1w63fwj9g0x2">
                    <a href={link.href} onClick={() => setOpen(false)} className="block border-b border-navy-900/5 py-3 font-display text-base font-bold text-navy-900" data-id="element-239" data-mp="1v4pnkav10bn">
                      {t(link.labelKey)}
                    </a>
                  </li>)}
              </ul>

              <div className="mt-4 flex items-center gap-3" data-id="element-240" data-mp="28b4ov2am12kl">
                <a href="#contact" onClick={() => setOpen(false)} className="flex-1 rounded-md bg-amber-600 px-4 py-3 text-center text-sm font-bold text-white" data-id="element-241" data-mp="2d84whhn3yddt">
                  {t('nav.cta')}
                </a>
                <div className="flex items-center rounded-md border border-navy-900/15 p-0.5" role="group" aria-label={t('nav.language')} data-id="element-242" data-mp="2r8nxi4t5oj6h">
                  {(['en', 'es'] as const).map(code => <button key={code} type="button" onClick={() => setLang(code)} aria-pressed={lang === code} className={`rounded px-3 py-2 text-xs font-bold uppercase ${lang === code ? 'bg-navy-900 text-white' : 'text-navy-900/60'}`} data-id="element-243" data-mp="310busmce8ufv">
                      {code}
                    </button>)}
                </div>
              </div>

              <a href={BUSINESS.phoneHref} className="mt-3 flex items-center justify-center gap-2 rounded-md border border-navy-900/15 py-3 text-sm font-bold text-navy-900" data-id="element-244" data-mp="1om2cq1yiaod3">
                <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-245" data-mp="1xl7av765ofoq" />
                {BUSINESS.phoneDisplay}
              </a>
            </nav>
          </motion.div> : null}
      </AnimatePresence>
    </header>;
}